import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { User, Building2, Globe, Camera, Upload, Trash2, Save, CheckCircle, Loader2, Lock, Eye, EyeOff, ShieldCheck, Phone, FileText, Ship, MapPin, XCircle, Clock, BadgeCheck, Sparkles, Building, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import { VerificationStatus, FullyVerifiedBadge, PhoneVerifiedBadge, GSTVerifiedBadge, IECVerifiedBadge } from '@/components/VerificationBadges';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import DetailedProfileProgress from '@/components/DetailedProfileProgress';
import { getCountryFlag, getCountryName } from '@/utils/countryFlags';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Profile() {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef(null);
  
  // Verification states
  const [verificationData, setVerificationData] = useState({
    phone: '',
    gstin: '',
    iec: ''
  });
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [verifyingGstin, setVerifyingGstin] = useState(false);
  const [submittingIec, setSubmittingIec] = useState(false);
  const [editingPhone, setEditingPhone] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [updatingPhone, setUpdatingPhone] = useState(false);
  const [gstinDetails, setGstinDetails] = useState(null); // Store GSTIN details after verification
  
  // Delete confirmation states
  const [showDeleteGstinDialog, setShowDeleteGstinDialog] = useState(false);
  const [showDeleteIecDialog, setShowDeleteIecDialog] = useState(false);
  const [deletingGstin, setDeletingGstin] = useState(false);
  const [deletingIec, setDeletingIec] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    country: '',
    about: '',
    nature_of_business: '',
    annual_turnover: '',
    business_address: '',
    website: ''
  });
  
  // AI Generation states
  const [generatingAbout, setGeneratingAbout] = useState(false);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiInputs, setAiInputs] = useState({
    key_products: '',
    years_in_business: '',
    key_strengths: '',
    target_markets: '',
    certifications: ''
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchUser();
    // Check if we should show password dialog (from header dropdown link)
    if (searchParams.get('tab') === 'password') {
      setShowPasswordDialog(true);
      // Clean up the URL
      setSearchParams({});
    }
  }, [searchParams]);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setFormData({
        full_name: response.data.full_name || '',
        company_name: response.data.company_name || '',
        country: response.data.country || '',
        about: response.data.about || '',
        nature_of_business: response.data.nature_of_business || '',
        annual_turnover: response.data.annual_turnover || '',
        business_address: response.data.business_address || '',
        website: response.data.website || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/auth/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/auth/upload-logo`, 
          { logo_data: base64Data },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setUser(prev => ({ ...prev, company_logo: response.data.logo_url }));
        toast.success('Logo uploaded successfully');
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload logo');
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/auth/remove-logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(prev => ({ ...prev, company_logo: null }));
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  // AI Company Description Generator
  const handleGenerateAbout = async () => {
    if (!formData.company_name) {
      toast.error('Please enter your company name first');
      return;
    }
    
    setGeneratingAbout(true);
    setShowAiDialog(false);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai/generate-company-description`, {
        company_name: formData.company_name,
        nature_of_business: formData.nature_of_business,
        country: formData.country,
        products: user?.products_count || 0,
        // Additional context from AI dialog
        key_products: aiInputs.key_products,
        years_in_business: aiInputs.years_in_business,
        key_strengths: aiInputs.key_strengths,
        target_markets: aiInputs.target_markets,
        certifications: aiInputs.certifications
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFormData(prev => ({ ...prev, about: response.data.description }));
      toast.success('Company description generated! Feel free to edit it.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate description');
    } finally {
      setGeneratingAbout(false);
    }
  };
  
  const openAiDialog = () => {
    if (!formData.company_name) {
      toast.error('Please enter your company name first');
      return;
    }
    setShowAiDialog(true);
  };

  const handleChangePassword = async () => {
    // Validate
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.current_password === passwordData.new_password) {
      toast.error('New password must be different from current password');
      return;
    }

    setChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      setShowPasswordDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Phone Verification Handler
  const handleVerifyPhone = async () => {
    if (!verificationData.phone) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setVerifyingPhone(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/verification/verify-phone-profile`, {
        phone: verificationData.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Phone number verified successfully!');
      setVerificationData({...verificationData, phone: ''});
      fetchUser(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify phone number');
    } finally {
      setVerifyingPhone(false);
    }
  };

  // Phone Update Handler (for editing verified phone)
  const handleUpdatePhone = async () => {
    if (!newPhoneNumber) {
      toast.error('Please enter a phone number');
      return;
    }
    
    setUpdatingPhone(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/verification/update-phone`, {
        phone: newPhoneNumber
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Phone number updated successfully!');
      setNewPhoneNumber('');
      setEditingPhone(false);
      fetchUser(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update phone number');
    } finally {
      setUpdatingPhone(false);
    }
  };

  // GSTIN Verification Handler
  const handleVerifyGstin = async () => {
    if (!verificationData.gstin || verificationData.gstin.length !== 15) {
      toast.error('Please enter a valid 15-character GSTIN');
      return;
    }
    
    setVerifyingGstin(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/verification/verify-gstin-profile`, {
        gstin: verificationData.gstin
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store the GSTIN details from the response
      if (response.data.details) {
        setGstinDetails(response.data.details);
      }
      
      toast.success('GSTIN submitted for review! Our team will verify within 24-48 hours.');
      setVerificationData({...verificationData, gstin: ''});
      fetchUser(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit GSTIN');
    } finally {
      setVerifyingGstin(false);
    }
  };

  // IEC Submission Handler
  const handleSubmitIec = async () => {
    if (!verificationData.iec || verificationData.iec.length !== 10) {
      toast.error('Please enter a valid 10-character IEC code');
      return;
    }
    
    setSubmittingIec(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/verification/submit-iec`, {
        iec_code: verificationData.iec
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('IEC code submitted for admin review!');
      setVerificationData({...verificationData, iec: ''});
      fetchUser(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit IEC code');
    } finally {
      setSubmittingIec(false);
    }
  };

  // Delete GSTIN Handler
  const handleDeleteGstin = async () => {
    setDeletingGstin(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/verification/gstin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('GSTIN removed successfully');
      setGstinDetails(null);
      setShowDeleteGstinDialog(false);
      fetchUser(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove GSTIN');
    } finally {
      setDeletingGstin(false);
    }
  };

  // Delete IEC Handler
  const handleDeleteIec = async () => {
    setDeletingIec(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/verification/iec`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('IEC code removed successfully');
      setShowDeleteIecDialog(false);
      fetchUser(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove IEC code');
    } finally {
      setDeletingIec(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8 py-6 pb-20 md:pb-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold font-heading">Profile Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your account and company information</p>
        </div>

        {/* Detailed Profile Progress - Expandable */}
        {user?.id && (
          <DetailedProfileProgress 
            userId={user.id} 
            userRole={user.role || 'seller'} 
          />
        )}

        {/* Profile Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              {/* Logo/Avatar Section */}
              <div className="relative group">
                <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                  <AvatarImage src={user?.company_logo} alt={user?.company_name || user?.full_name} />
                  <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                    {getInitials(user?.company_name || user?.full_name)}
                  </AvatarFallback>
                </Avatar>
                
                {/* Upload Overlay */}
                <div 
                  className="absolute inset-0 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadingLogo ? (
                    <Loader2 className="h-6 w-6 text-white animate-spin" />
                  ) : (
                    <Camera className="h-6 w-6 text-white" />
                  )}
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="logo-upload-input"
                />
              </div>
              
              <div className="flex-1">
                <CardTitle className="text-xl flex items-center gap-2">
                  {user?.company_name || user?.full_name}
                  {/* Premium Verified Badge - shows when all 3 verifications complete */}
                  <FullyVerifiedBadge 
                    phoneVerified={user?.phone_verified}
                    gstinVerified={user?.gstin_verified}
                    iecStatus={user?.iec_status}
                    size="default"
                  />
                </CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <span>{user?.email}</span>
                  <Badge variant={user?.role === 'seller' ? 'default' : 'secondary'} className="capitalize">
                    {user?.role}
                  </Badge>
                </CardDescription>
                {/* Individual Verification Badges */}
                {user?.role === 'seller' && (
                  <div className="flex items-center gap-2 mt-2">
                    <PhoneVerifiedBadge verified={user?.phone_verified} size="default" showLabel />
                    <GSTVerifiedBadge verified={user?.gstin_verified} size="default" showLabel />
                    <IECVerifiedBadge status={user?.iec_status} size="default" showLabel />
                  </div>
                )}
              </div>
            </div>
            
            {/* Logo Actions */}
            <div className="flex gap-2 mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
                data-testid="upload-logo-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                {user?.company_logo ? 'Change Logo' : 'Upload Logo'}
              </Button>
              {user?.company_logo && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRemoveLogo}
                  className="text-destructive hover:text-destructive"
                  data-testid="remove-logo-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Verification Status Card - Sellers Only */}
        {user?.role === 'seller' && (
          <Card data-testid="verification-status-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Verification Status
              </CardTitle>
              <CardDescription>
                Complete all verifications to get the Verified Seller badge on the marketplace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Phone Verification */}
              <div className={`p-4 rounded-lg border ${
                user?.phone_verified 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${user?.phone_verified ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Phone className={`h-5 w-5 ${user?.phone_verified ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${user?.phone_verified ? 'text-green-700' : 'text-gray-600'}`}>
                        Phone Number Verification
                      </p>
                      <p className={`text-xs ${user?.phone_verified ? 'text-green-600' : 'text-gray-400'}`}>
                        {user?.phone_verified ? `Verified: ${user?.phone}` : 'Verify your phone number'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {user?.phone_verified ? (
                      <>
                        <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setEditingPhone(true);
                            setNewPhoneNumber(user?.phone || '');
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                          data-testid="edit-phone-btn"
                        >
                          Edit
                        </Button>
                      </>
                    ) : (
                      <Badge variant="secondary">Not Verified</Badge>
                    )}
                  </div>
                </div>
                {/* Edit Phone Form (for verified users) */}
                {editingPhone && user?.phone_verified && (
                  <div className="mt-3 p-3 bg-white rounded-lg border">
                    <p className="text-sm font-medium mb-2">Update Phone Number</p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="+91 9876543210"
                        value={newPhoneNumber}
                        onChange={(e) => setNewPhoneNumber(e.target.value)}
                        className="max-w-xs"
                        data-testid="edit-phone-input"
                      />
                      <Button 
                        size="sm" 
                        onClick={handleUpdatePhone}
                        disabled={updatingPhone || !newPhoneNumber}
                        data-testid="update-phone-btn"
                      >
                        {updatingPhone ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
                      </Button>
                      <Button 
                        variant="ghost"
                        size="sm" 
                        onClick={() => {
                          setEditingPhone(false);
                          setNewPhoneNumber('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Note: Phone number will be updated without OTP verification for now.
                    </p>
                  </div>
                )}
                {/* Add Phone Form (for unverified users) */}
                {!user?.phone_verified && !editingPhone && (
                  <div className="flex gap-2 mt-3">
                    <Input
                      placeholder="+91 9876543210"
                      value={verificationData.phone}
                      onChange={(e) => setVerificationData({...verificationData, phone: e.target.value})}
                      className="max-w-xs"
                      data-testid="phone-verify-input"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleVerifyPhone}
                      disabled={verifyingPhone || !verificationData.phone}
                      data-testid="phone-verify-btn"
                    >
                      {verifyingPhone ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                    </Button>
                  </div>
                )}
              </div>

              {/* GSTIN Verification */}
              <div className={`p-4 rounded-lg border ${
                user?.gstin_verified 
                  ? 'bg-green-50 border-green-200' 
                  : user?.gstin_status === 'under_review'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      user?.gstin_verified ? 'bg-green-100' : 
                      user?.gstin_status === 'under_review' ? 'bg-amber-100' : 'bg-gray-100'
                    }`}>
                      <FileText className={`h-5 w-5 ${
                        user?.gstin_verified ? 'text-green-600' : 
                        user?.gstin_status === 'under_review' ? 'text-amber-600' : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${
                        user?.gstin_verified ? 'text-green-700' : 
                        user?.gstin_status === 'under_review' ? 'text-amber-700' : 'text-gray-600'
                      }`}>
                        GSTIN Verification
                      </p>
                      <p className={`text-xs ${
                        user?.gstin_verified ? 'text-green-600' : 
                        user?.gstin_status === 'under_review' ? 'text-amber-600' : 'text-gray-400'
                      }`}>
                        {user?.gstin_verified ? `Verified: ${user?.gstin}` : 
                         user?.gstin_status === 'under_review' ? `Under Review: ${user?.gstin}` : 
                         'Submit your GST Identification Number for verification'}
                      </p>
                    </div>
                  </div>
                  {user?.gstin_verified ? (
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setShowDeleteGstinDialog(true)}
                        data-testid="delete-gstin-btn"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Remove
                      </Button>
                    </div>
                  ) : user?.gstin_status === 'under_review' ? (
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-500"><Clock className="h-3 w-3 mr-1" />Under Review</Badge>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setShowDeleteGstinDialog(true)}
                        data-testid="cancel-gstin-btn"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Badge variant="secondary">Not Submitted</Badge>
                  )}
                </div>
                
                {/* Show GSTIN Details when verified */}
                {user?.gstin_verified && (user?.gstin_details || gstinDetails) && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-green-100">
                    <p className="text-xs font-medium text-gray-600 mb-2">Company Details from GST Portal:</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-muted-foreground text-xs">Trade Name:</span>
                        <p className="font-medium">{(user?.gstin_details || gstinDetails)?.tradeName || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">Legal Name:</span>
                        <p className="font-medium">{(user?.gstin_details || gstinDetails)?.legalName || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">Status:</span>
                        <p className={`font-medium ${(user?.gstin_details || gstinDetails)?.status?.toLowerCase() === 'active' ? 'text-green-600' : 'text-amber-600'}`}>
                          {(user?.gstin_details || gstinDetails)?.status || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-muted-foreground text-xs">Registration Date:</span>
                        <p className="font-medium">{(user?.gstin_details || gstinDetails)?.registrationDate || 'N/A'}</p>
                      </div>
                      {(user?.gstin_details || gstinDetails)?.businessType && (
                        <div className="sm:col-span-2">
                          <span className="text-muted-foreground text-xs">Business Type:</span>
                          <p className="font-medium">{(user?.gstin_details || gstinDetails)?.businessType}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Under Review message */}
                {user?.gstin_status === 'under_review' && !user?.gstin_verified && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Your GSTIN will be verified by our team within 24-48 hours
                    </p>
                  </div>
                )}
                
                {/* Input form - only show if not verified AND not under review */}
                {!user?.gstin_verified && user?.gstin_status !== 'under_review' && (
                  <div className="flex gap-2 mt-3">
                    <Input
                      placeholder="22AAAAA0000A1Z5"
                      value={verificationData.gstin}
                      onChange={(e) => setVerificationData({...verificationData, gstin: e.target.value.toUpperCase()})}
                      className="max-w-xs uppercase"
                      maxLength={15}
                      data-testid="gstin-verify-input"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleVerifyGstin}
                      disabled={verifyingGstin || !verificationData.gstin || verificationData.gstin.length !== 15}
                      data-testid="gstin-verify-btn"
                    >
                      {verifyingGstin ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit for Review'}
                    </Button>
                  </div>
                )}
              </div>

              {/* IEC Code Verification */}
              <div className={`p-4 rounded-lg border ${
                user?.iec_status === 'approved'
                  ? 'bg-green-50 border-green-200' 
                  : (user?.iec_status === 'pending' || user?.iec_status === 'under_review')
                    ? 'bg-amber-50 border-amber-200'
                    : user?.iec_status === 'rejected'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${
                      user?.iec_status === 'approved' ? 'bg-green-100' : 
                      (user?.iec_status === 'pending' || user?.iec_status === 'under_review') ? 'bg-amber-100' : 
                      user?.iec_status === 'rejected' ? 'bg-red-100' : 'bg-gray-100'
                    }`}>
                      <Ship className={`h-5 w-5 ${
                        user?.iec_status === 'approved' ? 'text-green-600' : 
                        (user?.iec_status === 'pending' || user?.iec_status === 'under_review') ? 'text-amber-600' : 
                        user?.iec_status === 'rejected' ? 'text-red-600' : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${
                        user?.iec_status === 'approved' ? 'text-green-700' : 
                        (user?.iec_status === 'pending' || user?.iec_status === 'under_review') ? 'text-amber-700' : 
                        user?.iec_status === 'rejected' ? 'text-red-700' : 'text-gray-600'
                      }`}>
                        IEC Code Verification
                      </p>
                      <p className={`text-xs ${
                        user?.iec_status === 'approved' ? 'text-green-600' : 
                        (user?.iec_status === 'pending' || user?.iec_status === 'under_review') ? 'text-amber-600' : 
                        user?.iec_status === 'rejected' ? 'text-red-600' : 'text-gray-400'
                      }`}>
                        {user?.iec_status === 'approved' ? `Approved: ${user?.iec_code}` : 
                         (user?.iec_status === 'pending' || user?.iec_status === 'under_review') ? `Under Review: ${user?.iec_code}` : 
                         user?.iec_status === 'rejected' ? 'Rejected - Please resubmit' : 
                         'Submit your Import Export Code for verification'}
                      </p>
                    </div>
                  </div>
                  {user?.iec_status === 'approved' ? (
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setShowDeleteIecDialog(true)}
                        data-testid="delete-iec-btn"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Remove
                      </Button>
                    </div>
                  ) : (user?.iec_status === 'pending' || user?.iec_status === 'under_review') ? (
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-500"><Clock className="h-3 w-3 mr-1" />Under Review</Badge>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setShowDeleteIecDialog(true)}
                        data-testid="cancel-iec-btn"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Cancel
                      </Button>
                    </div>
                  ) : user?.iec_status === 'rejected' ? (
                    <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>
                  ) : (
                    <Badge variant="secondary">Not Submitted</Badge>
                  )}
                </div>

                {/* Under Review message */}
                {(user?.iec_status === 'pending' || user?.iec_status === 'under_review') && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Your IEC will be verified by our team within 24-48 hours
                    </p>
                  </div>
                )}

                {/* Input form - only show if not approved AND not under review */}
                {(user?.iec_status !== 'approved' && user?.iec_status !== 'pending' && user?.iec_status !== 'under_review') && (
                  <div className="flex gap-2 mt-3">
                    <Input
                      placeholder="AAAAAAA000"
                      value={verificationData.iec}
                      onChange={(e) => setVerificationData({...verificationData, iec: e.target.value.toUpperCase()})}
                      className="max-w-xs uppercase"
                      maxLength={10}
                      data-testid="iec-verify-input"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleSubmitIec}
                      disabled={submittingIec || !verificationData.iec || verificationData.iec.length !== 10}
                      data-testid="iec-submit-btn"
                    >
                      {submittingIec ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit for Review'}
                    </Button>
                  </div>
                )}
                {user?.iec_status === 'rejected' && user?.iec_rejection_reason && (
                  <p className="text-xs text-red-600 mt-2">Reason: {user?.iec_rejection_reason}</p>
                )}
              </div>

              {/* Location Info */}
              {user?.district && (
                <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>Location: {user.district}, India</span>
                </div>
              )}

              {/* Help Text */}
              <p className="mt-4 text-xs text-muted-foreground">
                Complete verification improves your visibility and builds trust with buyers. 
                {!user?.phone_verified && ' Verify your phone number to get started.'}
                {user?.gstin_status === 'under_review' && ' Your GSTIN is under review by our team.'}
                {(user?.iec_status === 'pending' || user?.iec_status === 'under_review') && ' Your IEC is under review by our team.'}
                {user?.iec_status === 'not_submitted' && !user?.iec_code && ' Submit your IEC code to enable export features.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Verification Status Card - Buyers Only (Phone Verification) */}
        {user?.role === 'buyer' && (
          <Card data-testid="buyer-verification-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5" />
                Account Verification
              </CardTitle>
              <CardDescription>
                Verify your phone number to unlock all marketplace features
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`p-4 rounded-lg border ${
                user?.phone_verified 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${user?.phone_verified ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <Phone className={`h-5 w-5 ${user?.phone_verified ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${user?.phone_verified ? 'text-green-700' : 'text-gray-600'}`}>
                        Phone Number Verification
                      </p>
                      <p className={`text-xs ${user?.phone_verified ? 'text-green-600' : 'text-gray-400'}`}>
                        {user?.phone_verified ? `Verified: ${user?.phone}` : 'Verify your phone number for trusted buyer status'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {user?.phone_verified ? (
                      <>
                        <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Verified</Badge>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setEditingPhone(true);
                            setNewPhoneNumber(user?.phone || '');
                          }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                          data-testid="buyer-edit-phone-btn"
                        >
                          Edit
                        </Button>
                      </>
                    ) : (
                      <Badge variant="secondary">Not Verified</Badge>
                    )}
                  </div>
                </div>
                {/* Edit Phone Form (for verified buyers) */}
                {editingPhone && user?.phone_verified && (
                  <div className="mt-3 p-3 bg-white rounded-lg border">
                    <p className="text-sm font-medium mb-2">Update Phone Number</p>
                    <div className="flex gap-2">
                      <Input
                        placeholder="+91 9876543210"
                        value={newPhoneNumber}
                        onChange={(e) => setNewPhoneNumber(e.target.value)}
                        className="max-w-xs"
                        data-testid="buyer-edit-phone-input"
                      />
                      <Button 
                        size="sm" 
                        onClick={handleUpdatePhone}
                        disabled={updatingPhone || !newPhoneNumber}
                        data-testid="buyer-update-phone-btn"
                      >
                        {updatingPhone ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
                      </Button>
                      <Button 
                        variant="ghost"
                        size="sm" 
                        onClick={() => {
                          setEditingPhone(false);
                          setNewPhoneNumber('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
                {/* Add Phone Form (for unverified buyers) */}
                {!user?.phone_verified && !editingPhone && (
                  <div className="flex gap-2 mt-3">
                    <Input
                      placeholder="+91 9876543210"
                      value={verificationData.phone}
                      onChange={(e) => setVerificationData({...verificationData, phone: e.target.value})}
                      className="max-w-xs"
                      data-testid="buyer-phone-verify-input"
                    />
                    <Button 
                      size="sm" 
                      onClick={handleVerifyPhone}
                      disabled={verifyingPhone || !verificationData.phone}
                      data-testid="buyer-phone-verify-btn"
                    >
                      {verifyingPhone ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Profile Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
            <CardDescription>Update your personal and company details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Enter your full name"
                  data-testid="full-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Enter company name"
                    className="pl-10"
                    data-testid="company-name-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                    placeholder="Enter country"
                    className="pl-10"
                    data-testid="country-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">Email cannot be changed</p>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="flex justify-end">
              <Button 
                onClick={handleSaveProfile} 
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Company Profile Section - Sellers Only */}
        {user?.role === 'seller' && (
          <Card data-testid="company-profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Company Profile
              </CardTitle>
              <CardDescription>
                This information will be displayed on your products and seller profile
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* About the Company */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="about">About the Company</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openAiDialog}
                    disabled={generatingAbout || !formData.company_name}
                    className="flex items-center gap-1"
                    data-testid="generate-about-btn"
                  >
                    {generatingAbout ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Sparkles className="h-4 w-4" />
                    )}
                    {generatingAbout ? 'Generating...' : 'Generate with AI'}
                  </Button>
                </div>
                <Textarea
                  id="about"
                  value={formData.about}
                  onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                  placeholder="Write a detailed description about your company, its history, expertise, and what makes it unique..."
                  rows={5}
                  className="resize-none"
                  data-testid="company-about-input"
                />
                <p className="text-xs text-muted-foreground">
                  Tip: A good company description helps build trust with potential buyers
                </p>
              </div>

              {/* Business Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nature_of_business">Nature of Business</Label>
                  <Select
                    value={formData.nature_of_business}
                    onValueChange={(value) => setFormData({ ...formData, nature_of_business: value })}
                  >
                    <SelectTrigger data-testid="nature-of-business-select">
                      <SelectValue placeholder="Select nature of business" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Manufacturer">Manufacturer</SelectItem>
                      <SelectItem value="Exporter">Exporter</SelectItem>
                      <SelectItem value="Manufacturer & Exporter">Manufacturer & Exporter</SelectItem>
                      <SelectItem value="Trader">Trader</SelectItem>
                      <SelectItem value="Supplier">Supplier</SelectItem>
                      <SelectItem value="Wholesaler">Wholesaler</SelectItem>
                      <SelectItem value="Distributor">Distributor</SelectItem>
                      <SelectItem value="Service Provider">Service Provider</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="annual_turnover">Annual Turnover</Label>
                  <Select
                    value={formData.annual_turnover}
                    onValueChange={(value) => setFormData({ ...formData, annual_turnover: value })}
                  >
                    <SelectTrigger data-testid="annual-turnover-select">
                      <SelectValue placeholder="Select annual turnover" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Less than ₹40 Lakh">Less than ₹40 Lakh</SelectItem>
                      <SelectItem value="₹40 Lakh - ₹1 Crore">₹40 Lakh - ₹1 Crore</SelectItem>
                      <SelectItem value="₹1 Crore - ₹5 Crore">₹1 Crore - ₹5 Crore</SelectItem>
                      <SelectItem value="₹5 Crore - ₹25 Crore">₹5 Crore - ₹25 Crore</SelectItem>
                      <SelectItem value="₹25 Crore - ₹100 Crore">₹25 Crore - ₹100 Crore</SelectItem>
                      <SelectItem value="More than ₹100 Crore">More than ₹100 Crore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Business Address */}
              <div className="space-y-2">
                <Label htmlFor="business_address">Business Address</Label>
                <Textarea
                  id="business_address"
                  value={formData.business_address}
                  onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
                  placeholder="Enter your complete business address..."
                  rows={2}
                  className="resize-none"
                  data-testid="business-address-input"
                />
              </div>

              {/* Website */}
              <div className="space-y-2">
                <Label htmlFor="website">Business Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.yourcompany.com"
                    className="pl-10"
                    data-testid="website-input"
                  />
                </div>
              </div>

              <Button 
                onClick={handleSaveProfile} 
                disabled={saving}
                className="w-full md:w-auto"
                data-testid="save-company-profile-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Company Profile
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Account Info */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Account Type</p>
                <p className="font-medium capitalize">{user?.role}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Verification Status</p>
                <Badge variant={user?.is_verified ? 'default' : 'secondary'}>
                  {user?.verification_status || 'Pending'}
                </Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Member Since</p>
                <p className="font-medium">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">User ID</p>
                <p className="font-mono text-xs">{user?.id?.slice(0, 8)}...</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Change Password - Now available in header dropdown menu */}

        {/* Change Password Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </DialogTitle>
              <DialogDescription>
                Update your account password. You'll receive an email confirmation.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                    placeholder="Enter current password"
                    className="pl-10 pr-10"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="new_password"
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                    placeholder="Enter new password"
                    className="pl-10 pr-10"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    placeholder="Confirm new password"
                    className="pl-10 pr-10"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>After changing your password, your old password will no longer work.</span>
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleChangePassword} 
                disabled={changingPassword || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password}
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* AI Company Description Generator Dialog */}
        <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Generate Company Description with AI
              </DialogTitle>
              <DialogDescription>
                Help us understand your business better to generate a personalized description
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="space-y-2">
                <Label htmlFor="ai_key_products">Key Products/Services *</Label>
                <Textarea
                  id="ai_key_products"
                  value={aiInputs.key_products}
                  onChange={(e) => setAiInputs({ ...aiInputs, key_products: e.target.value })}
                  placeholder="e.g., Organic spices, Basmati rice, Textiles, Leather goods..."
                  rows={2}
                  className="resize-none"
                  data-testid="ai-key-products-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ai_years">Years in Business</Label>
                  <Input
                    id="ai_years"
                    value={aiInputs.years_in_business}
                    onChange={(e) => setAiInputs({ ...aiInputs, years_in_business: e.target.value })}
                    placeholder="e.g., 10 years"
                    data-testid="ai-years-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ai_markets">Target Markets</Label>
                  <Input
                    id="ai_markets"
                    value={aiInputs.target_markets}
                    onChange={(e) => setAiInputs({ ...aiInputs, target_markets: e.target.value })}
                    placeholder="e.g., USA, Europe, Middle East"
                    data-testid="ai-markets-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai_strengths">Key Strengths/USPs</Label>
                <Textarea
                  id="ai_strengths"
                  value={aiInputs.key_strengths}
                  onChange={(e) => setAiInputs({ ...aiInputs, key_strengths: e.target.value })}
                  placeholder="e.g., ISO certified, Direct from farm, 24/7 support, Competitive pricing..."
                  rows={2}
                  className="resize-none"
                  data-testid="ai-strengths-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ai_certifications">Certifications (if any)</Label>
                <Input
                  id="ai_certifications"
                  value={aiInputs.certifications}
                  onChange={(e) => setAiInputs({ ...aiInputs, certifications: e.target.value })}
                  placeholder="e.g., ISO 9001, FSSAI, Organic certified..."
                  data-testid="ai-certifications-input"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 flex items-start gap-2">
                  <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>The more details you provide, the more accurate and personalized your company description will be!</span>
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAiDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleGenerateAbout}
                disabled={generatingAbout}
                data-testid="confirm-generate-btn"
              >
                {generatingAbout ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Description
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete GSTIN Confirmation Dialog */}
        <AlertDialog open={showDeleteGstinDialog} onOpenChange={setShowDeleteGstinDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove GSTIN Verification?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove your GSTIN ({user?.gstin}) from your profile. You can verify a new GSTIN anytime.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingGstin}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteGstin}
                disabled={deletingGstin}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingGstin ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Remove GSTIN
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete IEC Confirmation Dialog */}
        <AlertDialog open={showDeleteIecDialog} onOpenChange={setShowDeleteIecDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove IEC Code?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove your IEC code ({user?.iec_code}) from your profile. You can submit a new IEC code for verification anytime.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingIec}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteIec}
                disabled={deletingIec}
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingIec ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Remove IEC
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
